import React, { useEffect, useState } from 'react';
import { MessageSquareText, Camera, Video, StickyNote, HelpCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { useStore } from '@/context/StoreContext';

// Reusable inquiry form used on product page + checkout.
// Props: open, onOpenChange, product (optional), orderNumber (optional), source ('product'|'checkout')
const TYPE_OPTIONS = [
  { key: 'photos', label: 'Request real photos', icon: Camera, hint: 'We will send actual product photos taken by our team.' },
  { key: 'video', label: 'Request a product video', icon: Video, hint: 'A short walk-around video so you can see it up close.' },
  { key: 'question', label: 'Ask a question', icon: HelpCircle, hint: 'Fabric, size, delivery timeline — anything at all.' },
  { key: 'note', label: 'Leave a note', icon: StickyNote, hint: 'Special instruction for our team.' },
];

export default function InquiryDialog({ open, onOpenChange, product, orderNumber, source = 'product', defaultType = 'photos' }) {
  const { user } = useStore();
  const [type, setType] = useState(defaultType);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(defaultType || 'photos');
    setMessage('');
    setName(user?.name || '');
    setEmail(user?.email || '');
    setPhone(user?.phone || '');
  }, [open, user, defaultType]);

  const submit = async () => {
    if (type === 'question' && !message.trim()) {
      toast.error('Please write your question');
      return;
    }
    if (!user && !email.trim() && !phone.trim()) {
      toast.error('Please share an email or phone so we can reach you');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        inquiry_type: type,
        message: message.trim(),
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        product_id: product?.id,
        product_slug: product?.slug,
        order_number: orderNumber || null,
        source,
      };
      const { data } = await api.post('/inquiries', payload);
      toast.success(data.message || 'Request sent. Our team will reach out shortly.');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send your request');
    } finally { setSubmitting(false); }
  };

  const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';
  const selected = TYPE_OPTIONS.find((t) => t.key === type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquareText size={18} /> {source === 'checkout' ? 'Need help before placing your order?' : 'Ask about this product'}</DialogTitle>
        </DialogHeader>

        {product && (
          <div className="flex items-center gap-3 rounded-xl bg-[hsl(var(--gaze-ivory))] p-3">
            <img src={product.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <div className="min-w-0"><p className="truncate text-sm font-semibold">{product.name}</p><p className="truncate text-xs text-muted-foreground">{product.sku}</p></div>
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-2">
          {TYPE_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const on = type === opt.key;
            return (
              <button key={opt.key} onClick={() => setType(opt.key)} data-testid={`inquiry-type-${opt.key}`}
                className={`flex items-start gap-2 rounded-xl border p-3 text-left text-xs transition ${on ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 ring-1 ring-[hsl(var(--primary))]' : 'border-border bg-white hover:border-[hsl(var(--gaze-gold))]'}`}>
                <Icon size={15} className={on ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'} />
                <span className="font-semibold">{opt.label}</span>
              </button>
            );
          })}
        </div>
        {selected && <p className="mt-2 text-xs text-muted-foreground">{selected.hint}</p>}

        <div className="mt-4 grid gap-2">
          <label className="text-xs font-medium">Your message {type !== 'question' && <span className="text-muted-foreground">(optional)</span>}</label>
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder={type === 'photos' ? 'e.g. Please send close-ups of the fabric.'
              : type === 'video' ? 'e.g. Could you show it in natural light?'
              : type === 'note' ? 'Any special instructions for our team.'
              : 'Type your question here…'}
            className={field} data-testid="inquiry-message" />
        </div>

        {!user && (
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <div><label className="text-xs font-medium">Name</label><input value={name} onChange={(e) => setName(e.target.value)} className={field} data-testid="inquiry-name" /></div>
            <div><label className="text-xs font-medium">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="inquiry-email" /></div>
            <div><label className="text-xs font-medium">Phone</label><input value={phone} onChange={(e) => setPhone(e.target.value)} className={field} data-testid="inquiry-phone" /></div>
          </div>
        )}
        {user && <p className="mt-2 text-xs text-muted-foreground">Signed in as <span className="font-semibold">{user.email}</span> — we&apos;ll respond via email or phone on record.</p>}

        <button onClick={submit} disabled={submitting} data-testid="inquiry-submit"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white transition hover:bg-[hsl(var(--gaze-green-bottle))] disabled:opacity-60">
          <Send size={14} /> {submitting ? 'Sending…' : 'Send request'}
        </button>
        <p className="mt-2 text-center text-[11px] text-muted-foreground">Genuine purchase help — not a live chat. Our team typically responds within a few hours.</p>
      </DialogContent>
    </Dialog>
  );
}
